import React, { useState, useEffect } from "react";
import { Card, Input, Button, Select } from "antd";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableTrainingComponentRow from "./SortableTrainingComponentRow";

const TrainingComponentManager = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTrainingComponent, setSelectedTrainingComponent] =
    useState(null);
  const [trainingComponentToDelete, setTrainingComponentToDelete] =
    useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  // State for form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    name: "",
    description: "",
  });

  // Pagination, search and status filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [trainingComponentList, setTrainingComponentList] = useState([]);

  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  // API hooks
  const { execute: fetchList, loading: loadingList } = useGet(
    `${API_ENDPOINTS.TRAINING_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&status=${statusFilter}`,
  );

  const { post, loading: creating } = usePost(API_ENDPOINTS.TRAINING_LIST);
  const { execute: getById, loading: loadingById } = useDynamicApi("GET");
  const { execute: putData, loading: updating } = useDynamicApi("PUT");
  const { execute: patchData, loading: updatingStatus } =
    useDynamicApi("PATCH");
  const { execute: deleteData, loading: deleting } = useDynamicApi("DELETE");
  const { execute: postData, loading: posting } = useDynamicApi("POST");

  // Loading state
  const loading =
    loadingList ||
    loadingById ||
    updating ||
    updatingStatus ||
    deleting ||
    isReordering ||
    posting;

  // Load data with current pagination and search
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        let data = response.data || [];
        // Sort by display_order when no search
        if (!searchTerm && statusFilter === "all") {
          data = data.sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0),
          );
        }
        setTrainingComponentList(data);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const reorderTrainingComponents = async (orderedIds) => {
    if (!orderedIds || orderedIds.length === 0) {
      Toast.error("No component IDs to reorder");
      return false;
    }
    try {
      setIsReordering(true);
      const response = await postData(API_ENDPOINTS.TRAINING_REORDER, { ordered_ids: orderedIds });
      if (response?.status === true) {
        Toast.success(response?.message || "Components reordered successfully!");
        return true;
      }
      Toast.error(response?.message || "Failed to reorder components");
      return false;
    } catch (error) {
      Toast.error(error.message || "Failed to reorder components");
      return false;
    } finally {
      setIsReordering(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);
    if (searchTerm || statusFilter !== "all") return;
    if (!over || active.id === over.id) return;
    const oldIndex = trainingComponentList.findIndex((i) => i.id === active.id);
    const newIndex = trainingComponentList.findIndex((i) => i.id === over.id);
    if (oldIndex !== newIndex) {
      const newOrderedList = arrayMove(trainingComponentList, oldIndex, newIndex);
      setTrainingComponentList(newOrderedList);
      const success = await reorderTrainingComponents(newOrderedList.map((i) => i.id));
      if (!success) setTrainingComponentList(trainingComponentList);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setIsDragging(false);
  };

  // Initial data fetch
  useEffect(() => {
    loadData();
  }, [currentPage, pageSize, statusFilter]);

  // Handle search - triggers when debounced value changes
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      is_active: true,
    });
    setFormErrors({
      name: "",
      description: "",
    });
    setSelectedTrainingComponent(null);
    setIsEditMode(false);
    setIsViewMode(false);
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === "") {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    } else if (formData.name.trim().length > 100) {
      errors.name = "Name must not exceed 100 characters";
    }

    if (formData.description && formData.description.length > 500) {
      errors.description = "Description must not exceed 500 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create
  const handleCreate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      Toast.error("Please fill the details before submitting");
      return;
    }

    try {
      const response = await post(formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || "Training Component created successfully!",
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        Toast.error(response?.message || "Failed to create Training Component");
      }
    } catch (error) {
      console.error("Error creating Training Component:", error);
      Toast.error(error.message || "Failed to create Training Component");
    }
  };

  // Handle update
  const handleUpdate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      Toast.error("Please fix the form errors");
      return;
    }

    try {
      const endpoint = `${API_ENDPOINTS.TRAINING_UPDATE}/${selectedTrainingComponent.id}`;
      const response = await putData(endpoint, formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || "Training Component updated successfully!",
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        Toast.error(response?.message || "Failed to update Training Component");
      }
    } catch (error) {
      console.error("Error updating Training Component:", error);
      Toast.error(error.message || "Failed to update Training Component");
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (item) => {
    try {
      const newStatus = !item.is_active;
      const endpoint = `${API_ENDPOINTS.TRAINING_UPDATE}/${item.id}`;

      const response = await patchData(endpoint, {
        is_active: newStatus,
      });

      if (response?.status === true) {
        Toast.success(
          response?.message ||
            `Training Component ${newStatus ? "activated" : "deactivated"} successfully!`,
        );
        await loadData();
      } else {
        Toast.error(response?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Toast.error(error.message || "Failed to update status");
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (item) => {
    setTrainingComponentToDelete(item);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!trainingComponentToDelete) return;

    try {
      const endpoint = `${API_ENDPOINTS.TRAINING_UPDATE}/${trainingComponentToDelete.id}`;
      const response = await deleteData(endpoint);

      if (response?.status === true) {
        Toast.success(
          response?.message || "Training Component deleted successfully!",
        );
        await loadData();
      } else {
        Toast.error(response?.message || "Failed to delete Training Component");
      }
    } catch (error) {
      console.error("Error deleting Training Component:", error);
      Toast.error(error.message || "Failed to delete Training Component");
    } finally {
      setIsDeleteModalOpen(false);
      setTrainingComponentToDelete(null);
    }
  };

  // Open modal for create
  const openCreateModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  // Open modal for edit
  const openEditModal = async (item) => {
    try {
      const endpoint = `${API_ENDPOINTS.TRAINING_UPDATE}/${item.id}`;
      const response = await getById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedTrainingComponent(response.data);
        setFormData({
          name: response.data.name,
          description: response.data.description,
          is_active: response.data.is_active,
        });
        setIsEditMode(true);
        setIsViewMode(false);
        setIsModalOpen(true);
      } else {
        Toast.error("Failed to fetch Training Component details");
      }
    } catch (error) {
      console.error("Error fetching Training Component details:", error);
      Toast.error(error.message || "Failed to fetch details");
    }
  };

  // Open modal for view
  const openViewModal = async (item) => {
    try {
      const endpoint = `${API_ENDPOINTS.TRAINING_UPDATE}/${item.id}`;
      const response = await getById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedTrainingComponent(response.data);
        setFormData({
          name: response.data.name,
          description: response.data.description,
          is_active: response.data.is_active,
        });
        setIsEditMode(false);
        setIsViewMode(true);
        setIsModalOpen(true);
      } else {
        Toast.error("Failed to fetch Training Component details");
      }
    } catch (error) {
      console.error("Error fetching Training Component details:", error);
      Toast.error(error.message || "Failed to fetch details");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isDragEnabled = !searchTerm && statusFilter === "all" && !loading;
  const activeItem = activeId ? trainingComponentList.find((i) => i.id === activeId) : null;
  const hasFilters = searchTerm || statusFilter !== "all";

  return (
    <>
      <Card
        title="Training Component Manager"
        extra={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!isDragEnabled && (searchTerm || statusFilter !== "all") && (
              <span style={{ fontSize: 13, color: "#856404", background: "#fff3cd", padding: "6px 12px", borderRadius: 4 }}>Clear filters to reorder</span>
            )}
            <Button type="primary" onClick={openCreateModal} disabled={loading}>+ Create New Training Component</Button>
          </span>
        }
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <Input placeholder="Search by name or description..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} allowClear style={{ width: 280 }} disabled={loading} />
          <Select value={statusFilter} onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }} style={{ width: 140 }} options={[{ value: "all", label: "All Status" }, { value: "active", label: "Active Only" }, { value: "inactive", label: "Inactive Only" }]} disabled={loading} />
          <Button onClick={() => { setSearchTerm(""); setStatusFilter("all"); setCurrentPage(1); }} disabled={loading}>Reset Filters</Button>
        </div>

        <div style={{ position: "relative", background: "#fff", borderRadius: 8, overflow: "hidden" }}>
          {loadingList && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
              <span style={{ border: "3px solid #f0f0f0", borderTopColor: "#066744", borderRadius: "50%", width: 40, height: 40, animation: "spin 0.8s linear infinite" }} />
            </div>
          )}
          {isReordering && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9, pointerEvents: "none" }}>
              <span style={{ background: "#fff", padding: "8px 16px", borderRadius: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>Saving order...</span>
            </div>
          )}

          <div className="table-scroll-wrap">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            <table className="data-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafafa", borderBottom: "2px solid #eee" }}>
                  <th style={{  minWidth:"40px" , padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 14 }} />
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 14  }}>Name</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 14 }}>Description</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 14 }}>Created At</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 14 }}>Updated At</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 14 }}>Status</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 14 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loadingList && trainingComponentList.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#666" }}>{hasFilters ? "No matching entries found" : "No Training Component entries found"}</td>
                  </tr>
                ) : (
                  <SortableContext items={trainingComponentList.map((i) => i.id)} strategy={verticalListSortingStrategy} disabled={!isDragEnabled}>
                    {trainingComponentList.map((item) => (
                      <SortableTrainingComponentRow
                        key={item.id}
                        item={item}
                        loading={loading}
                        updatingStatus={updatingStatus}
                        searchTerm={searchTerm}
                        formatDate={formatDate}
                        handleToggleActive={handleToggleActive}
                        openViewModal={openViewModal}
                        openEditModal={openEditModal}
                        openDeleteModal={openDeleteModal}
                        isDragging={isDragging && activeId === item.id}
                        isDragEnabled={isDragEnabled}
                      />
                    ))}
                  </SortableContext>
                )}
              </tbody>
            </table>

            <DragOverlay>
              {activeItem && (
                <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: 8 }}>
                  <tbody>
                    <SortableTrainingComponentRow item={activeItem} loading={loading} updatingStatus={updatingStatus} searchTerm={searchTerm} formatDate={formatDate} handleToggleActive={handleToggleActive} openViewModal={openViewModal} openEditModal={openEditModal} openDeleteModal={openDeleteModal} isDragging isOverlay isDragEnabled={isDragEnabled} />
                  </tbody>
                </table>
              )}
            </DragOverlay>
          </DndContext>
          </div>

          {!loadingList && trainingComponentList.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "16px 0", borderTop: "1px solid #eee" }}>
              <span style={{ fontSize: 14, color: "#666" }}>Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Previous</Button>
                <Button type="primary" disabled>{currentPage}</Button>
                <Button disabled={currentPage >= Math.ceil(totalCount / pageSize)} onClick={() => setCurrentPage((p) => p + 1)}>Next</Button>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} style={{ padding: "6px 12px", borderRadius: 4, border: "1px solid #d9d9d9" }}>
                  {[5, 10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Create/Edit/View Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isViewMode
              ? "Training Component Details"
              : isEditMode
                ? "Edit Training Component"
                : "Create New Training Component"
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={
            isEditMode
              ? updating
                ? "Updating..."
                : "Update"
              : creating
                ? "Creating..."
                : "Create"
          }
          showConfirm={!isViewMode}
          confirmDisabled={creating || updating}
        >
          <form
            className="modal-form"
            onSubmit={isEditMode ? handleUpdate : handleCreate}
          >
            <div className="form-group">
              <label htmlFor="name">
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter Training Component name"
                required
                disabled={isViewMode || creating || updating}
                className={formErrors.name ? "input-error" : ""}
              />
              {formErrors.name && (
                <div className="error-message">{formErrors.name}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description (optional)"
                disabled={isViewMode || creating || updating}
                rows="4"
                className={formErrors.description ? "input-error" : ""}
              />
              {formErrors.description && (
                <div className="error-message">{formErrors.description}</div>
              )}
            </div>

            {!isViewMode && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    disabled={creating || updating}
                  />
                  <span>Active Component</span>
                </label>
              </div>
            )}

            {isViewMode && selectedTrainingComponent && (
              <div className="view-details">
                <div className="detail-row">
                  <strong>ID:</strong>
                  <span>{selectedTrainingComponent.id}</span>
                </div>
                <div className="detail-row">
                  <strong>Display Order:</strong>
                  <span>{selectedTrainingComponent.display_order || 0}</span>
                </div>
                <div className="detail-row">
                  <strong>Created At:</strong>
                  <span>
                    {formatDate(selectedTrainingComponent.created_at)}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Updated At:</strong>
                  <span>
                    {formatDate(selectedTrainingComponent.updated_at)}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>
                  <span
                    className={`status-badge ${
                      selectedTrainingComponent.is_active
                        ? "active"
                        : "inactive"
                    }`}
                  >
                    {selectedTrainingComponent.is_active
                      ? "Active"
                      : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && trainingComponentToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setTrainingComponentToDelete(null);
          }}
          title="Confirm Delete"
          onConfirm={handleDeleteConfirm}
          confirmText={deleting ? "Deleting..." : "Delete"}
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <h3>Are you sure you want to delete this Training Component?</h3>
            <div className="item-details">
              <p>
                <strong>Name:</strong> {trainingComponentToDelete.name}
              </p>
              <p>
                <strong>Description:</strong>{" "}
                {trainingComponentToDelete.description || "N/A"}
              </p>
              <p>
                <strong>Display Order:</strong>{" "}
                {trainingComponentToDelete.display_order || 0}
              </p>
              <p>
                <strong>Status:</strong>
                <span
                  className={`status-badge ${
                    trainingComponentToDelete.is_active ? "active" : "inactive"
                  }`}
                >
                  {trainingComponentToDelete.is_active ? "Active" : "Inactive"}
                </span>
              </p>
            </div>
            <p className="warning-text">
              This action cannot be undone. All data related to this Training
              Component will be permanently deleted.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TrainingComponentManager;
